import Dexie, { Table } from 'dexie';

import dayjs from 'dayjs';
import { QueryHistoryCmdParam } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromRenderer';
import { CoSyncInfo, CoSyncStrategy } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import { SyncOperationType } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/SyncOperationItem';
import { CoSyncTaskDB, getCoSyncTaskDB, SyncHistoryItem } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncTaskDB';
import { CoSyncReporter } from '@/renderer/views/desktop/FileManagement/FileSync/composition/CoSyncReporter';

// eslint-disable-next-line no-use-before-define
const waitUntilReady = function waitUntilReady(
  target: SyncHistoryDAO,
  name: PropertyKey,
  descriptor: PropertyDescriptor,
) {
  const fn = descriptor.value;
  descriptor.value = target.wrapReady(fn);
};

/**
 *
 *
 * @export
 * @class SyncHistoryDAO
 * 同步历史DAO
 */
export default class SyncHistoryDAO {
  private db: CoSyncTaskDB;
  ready: Promise<SyncHistoryDAO>;

  public static TolerateForMaxCount = 1000;
  public static MaxDbRecordCount = 10000;

  constructor(db: CoSyncTaskDB) {
    this.db = db;

    const limitCount = async () => {
      // 自清理属于内部触发的逻辑，在内部处理异常
      try {
        const tenDaysAgo = dayjs().subtract(30, 'day').valueOf();
        await db.history.where('finishTime').below(tenDaysAgo).delete();

        const cnt = await this.db.history.count();

        const needClear = cnt > SyncHistoryDAO.MaxDbRecordCount;
        CoSyncReporter.getInstance().reportHistoryBeforeClear(needClear, cnt);
        if (needClear) {
          await db.history.limit(cnt - SyncHistoryDAO.MaxDbRecordCount).delete();

          const cntAfterClear = await this.db.history.count();
          CoSyncReporter.getInstance().reportHistoryAfterClear(cnt, cntAfterClear);
        }
      } catch (error: any) {
        logger.ui.warn(`SyncHistoryDAO limitCount error: ${error}`);
      }

      return this;
    };
    this.ready = limitCount();
  }

  // eslint-disable-next-line class-methods-use-this
  public wrapReady(fn: Function) {
    return function wrapReady(...args: any[]): Promise<any> {
      // @ts-ignore
      return this.ready.then(() => fn.apply(this, args));
    };
  }

  @waitUntilReady
  public async add(historyItem: SyncHistoryItem): Promise<number> {
    return this.db.history.add(historyItem);
  }

  @waitUntilReady
  public async bulkAdd(historyItemList: SyncHistoryItem[], shouldLimitCount: boolean): Promise<number> {
    let itemList = historyItemList;
    const toAddCount = itemList.length;

    if (toAddCount >= SyncHistoryDAO.MaxDbRecordCount) {
      // 这里的clear和bulkAdd最好是加事务，避免因为异步导致实际添加的数量超过了我们的限制
      // 目前在启动场景也进行了数量限制，这里为了避免引入复杂度先不加事务
      await this.db.history.clear();
      itemList = itemList.slice(0, SyncHistoryDAO.MaxDbRecordCount);
    } else {
      if (shouldLimitCount) {
        const currCnt = await this.db.history.count();

        if (currCnt + toAddCount >= SyncHistoryDAO.MaxDbRecordCount + SyncHistoryDAO.TolerateForMaxCount) {
          const deleteCount = currCnt + toAddCount - SyncHistoryDAO.MaxDbRecordCount;
          await this.db.history.limit(deleteCount > 0 ? deleteCount : 0).delete();
        }
      }
    }

    const last_key = await this.db.history.bulkAdd(itemList);
    return last_key;
  }

  @waitUntilReady
  public async remove(id: number): Promise<void> {
    return this.db.history.delete(id);
  }

  @waitUntilReady
  public async removeAll(): Promise<void> {
    return this.db.history.clear();
  }

  @waitUntilReady
  public async getAllHistoryItems(): Promise<SyncHistoryItem[]> {
    return await this.db.history.toArray();
  }

  @waitUntilReady
  public async queryHistory(param: QueryHistoryCmdParam): Promise<SyncHistoryItem[]> {
    logger.ui.info('queryHistory start', param);
    const { historyId, queryType } = param;
    const belowHistoryId = queryType === 'below' ? historyId : undefined;
    const aboveHistoryId = queryType === 'above' ? historyId : undefined;
    const { count } = param;

    let items: SyncHistoryItem[] = [];
    if (belowHistoryId) {
      items = await this.db.history.where('id').below(belowHistoryId).limit(count).reverse().toArray();
    } else if (aboveHistoryId) {
      items = await this.db.history.where('id').above(aboveHistoryId).limit(count).reverse().toArray();
    } else {
      items = await this.db.history.reverse().limit(count).toArray();
    }
    logger.ui.info('queryHistory finish', param);
    return items;
  }

  @waitUntilReady
  public async getSkippedInvalidFiles(): Promise<SyncHistoryItem[]> {
    const allItems = await this.db.history.toArray();
    return allItems.filter(item => item.operate === SyncOperationType.SKIP_INVALID_FILENAME && item.isSkipped === true);
  }

  @waitUntilReady
  public async removeSkippedInvalidFile(relativePath: string): Promise<void> {
    await this.db.history
      .filter(
        item =>
          item.relativePath === relativePath &&
          item.operate === SyncOperationType.SKIP_INVALID_FILENAME &&
          item.isSkipped === true,
      )
      .delete();
  }
}
